import { Component } from 'react';
import PropTypes from 'prop-types';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UIButton from 'UIComponents/button/UIButton';
import UILoadingButton from 'UIComponents/button/UILoadingButton';
import UIDialogBody from 'UIComponents/dialog/UIDialogBody';
import UIDialogCloseButton from 'UIComponents/dialog/UIDialogCloseButton';
import UIDialogFooter from 'UIComponents/dialog/UIDialogFooter';
import UIDialogHeader from 'UIComponents/dialog/UIDialogHeader';
import UIModalPanel from 'UIComponents/dialog/UIModalPanel';
import H2 from 'UIComponents/elements/headings/H2';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import { TeamOptionType, UserOptionType } from '../types/OptionTypes';
import UsersAndTeamsSelector from './UsersAndTeamsSelector';
import { Text } from 'foundations-components/components/Text';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
class UsersAndTeamsAssetAccessPanel extends Component {
  constructor(...args) {
    super(...args);
    this.state = {
      selectedUserIds: [],
      selectedTeamIds: []
    };
    this.handleSave = () => {
      const {
        selectedTeamIds,
        selectedUserIds
      } = this.state;
      this.props.onSave({
        selectedTeamIds,
        selectedUserIds
      });
    };
  }
  renderSave() {
    const {
      selectedTeamIds,
      selectedUserIds
    } = this.state;
    const {
      allowPrivate,
      showSaveLoading
    } = this.props;
    const hasUsersButNoTeams = selectedTeamIds.length === 0 && selectedUserIds.length > 0;
    const button = /*#__PURE__*/_jsx(UILoadingButton, {
      "data-test-id": "confirm-button",
      use: "primary",
      onClick: this.handleSave,
      loading: showSaveLoading,
      disabled: hasUsersButNoTeams && !allowPrivate,
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "ui-asset-management-lib.assetAccessPanel.buttonLabels.save"
      })
    });
    if (hasUsersButNoTeams && !allowPrivate) {
      return /*#__PURE__*/_jsx(UITooltip, {
        placement: "left",
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "ui-asset-management-lib.tooltips.usersButNoTeamsError"
        }),
        children: button
      });
    }
    return button;
  }
  render() {
    const {
      allowPrivate,
      allowSetEditLevel,
      assetTypeDisplayName,
      userHasTeamAssignmentScope,
      assignedUserIds,
      assignedTeamIds,
      teams,
      description,
      requestsLoading,
      requestsError,
      useAccessLanguage,
      allowedUserIds,
      disallowedUserFormattedMessageKey,
      allowedTeamIds,
      disallowedTeamFormattedMessage,
      disableAccessToEveryone,
      disableAccessToEveryoneFormattedMessage
    } = this.props;
    return /*#__PURE__*/_jsxs(UIModalPanel, {
      "data-test-id": "users-and-teams-asset-access-panel",
      children: [/*#__PURE__*/_jsxs(UIDialogHeader, {
        children: [/*#__PURE__*/_jsx(UIDialogCloseButton, {
          onClick: this.props.onCancel
        }), /*#__PURE__*/_jsx(H2, {
          children: assetTypeDisplayName ? /*#__PURE__*/_jsx(FormattedMessage, {
            message: "ui-asset-management-lib.assetAccessPanel.title",
            options: {
              assetType: assetTypeDisplayName
            }
          }) : /*#__PURE__*/_jsx(FormattedMessage, {
            message: "ui-asset-management-lib.assetAccessPanel.defaultTitle"
          })
        })]
      }), /*#__PURE__*/_jsxs(UIDialogBody, {
        children: [/*#__PURE__*/_jsx(Text, {
          marginBlockEnd: "500",
          as: "p",
          children: description
        }), /*#__PURE__*/_jsx(UsersAndTeamsSelector, {
          allowPrivate: allowPrivate,
          allowSetEditLevel: allowSetEditLevel,
          assignedUserIds: assignedUserIds,
          assignedTeamIds: assignedTeamIds,
          teams: teams,
          requestsLoading: requestsLoading,
          requestsError: requestsError,
          userHasTeamAssignmentScope: userHasTeamAssignmentScope,
          onUpdateUserIds: selectedUserIds => this.setState({
            selectedUserIds
          }),
          onUpdateTeamIds: selectedTeamIds => this.setState({
            selectedTeamIds
          }),
          fullWidth: true,
          useAccessLanguage: useAccessLanguage,
          allowedUserIds: allowedUserIds,
          disallowedUserFormattedMessageKey: disallowedUserFormattedMessageKey,
          allowedTeamIds: allowedTeamIds,
          disallowedTeamFormattedMessage: disallowedTeamFormattedMessage,
          disableAccessToEveryone: disableAccessToEveryone,
          disableAccessToEveryoneFormattedMessage: disableAccessToEveryoneFormattedMessage
        })]
      }), /*#__PURE__*/_jsxs(UIDialogFooter, {
        children: [this.renderSave(), /*#__PURE__*/_jsx(UIButton, {
          use: "secondary",
          onClick: this.props.onCancel,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "ui-asset-management-lib.assetAccessPanel.buttonLabels.cancel"
          })
        })]
      })]
    });
  }
}
UsersAndTeamsAssetAccessPanel.propTypes = {
  // The display name of the asset (e.g. Landing Page, Workflow, ect.)
  assetTypeDisplayName: PropTypes.string,
  // If the user can assign the asset to just themselves "Private to me.", also requires a user to assign at least one team when assigning users to an asset
  allowPrivate: PropTypes.bool,
  // If the user can make the asset "view only", or set it to "view and edit" for everyone. Allowing to set the edit level includes the ability to set private (TODO: Is that right, I think its fair...)
  allowSetEditLevel: PropTypes.bool,
  // An optional array of User ID's that are currently assigned to the asset
  assignedUserIds: PropTypes.arrayOf(PropTypes.number).isRequired,
  // An optional array of Team ID's that are currently assigned to the asset
  assignedTeamIds: PropTypes.arrayOf(PropTypes.number).isRequired,
  // An array of users in the portal
  /** @deprecated Component now handles calling users on its own */
  users: PropTypes.arrayOf(UserOptionType),
  // The team structure for the portal
  teams: PropTypes.arrayOf(TeamOptionType).isRequired,
  // On save, will dispatch an object with two arrays, an array of newly assigned users, and one of assigned teams.
  onSave: PropTypes.func.isRequired,
  // Function called if the user chooses to cancel changes, should be used to just close the panel.
  onCancel: PropTypes.func.isRequired,
  // Optional: Description of the modal, shown at the top above the options
  description: PropTypes.node,
  // Optional: If requests for either users or teams are loading to show a loading state
  requestsLoading: PropTypes.bool,
  // Optional: If requests for either users or teams have failed show a failure state.
  requestsError: PropTypes.bool,
  // Optional: If a save request is in progress and the panel is open, shows UILoadingDots and disables the save button to prevent multiple submissions
  showSaveLoading: PropTypes.bool,
  // ** Legacy behavior support **
  // If a user does not have "content-team-assignment" scope, but is on a team that is assigned to an asset, and that team has children
  // The user can:
  // - assign/unassign any child teams of the team they are a member of.
  // The user cannot:
  // - View the full team hierarchy
  // - Unassign their own team
  // - Change the base level of assignment of the asset
  // - Assign/unassign individual users, or view which users may already be assigned
  userHasTeamAssignmentScope: PropTypes.bool,
  // If wanting to use language "access" instead of "edit"
  useAccessLanguage: PropTypes.bool,
  // Optional: A list of users IDs which are allowed to be selected
  allowedUserIds: PropTypes.arrayOf(PropTypes.number),
  // Optional: The formatted message key which is used in a formatted message, displayed within the tooltip on users which are not selectable because of the allowedUserIds.
  disallowedUserFormattedMessageKey: PropTypes.string,
  // Optional: A list of team ids which are allowed to be selected in the tree
  allowedTeamIds: PropTypes.arrayOf(PropTypes.number),
  // Optional: The formatted message which is displayed within the tooltip on teams which are not selectable because of the allowedTeamIds. This function needs to return a FormattedMessage.
  disallowedTeamFormattedMessage: PropTypes.func,
  // Optional: a boolean flag that disables the various "everyone" access levels when set - default false
  disableAccessToEveryone: PropTypes.bool,
  // Optional: The formatted message which is displayed within the "everyone" access levels are disabled. This function needs to return a FormattedMessage.
  disableAccessToEveryoneFormattedMessage: PropTypes.func
};
UsersAndTeamsAssetAccessPanel.defaultProps = {
  allowPrivate: false,
  allowSetEditLevel: false,
  requestsError: false,
  requestsLoading: false,
  showSaveLoading: false,
  userHasTeamAssignmentScope: true,
  useAccessLanguage: false,
  description: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "ui-asset-management-lib.description.default"
  })
};
export default UsersAndTeamsAssetAccessPanel;